import { z } from "zod";

import { isAdwSchema } from "@/src/models/common-schema";
import { PharmacyRelatedDocumentType } from "@/src/models/constants/document-types";
import {
  MEDICATION_NAME_PROMPT,
  PHARMACY_COMPANY_NAME_PROMPT,
  SUPPLY_DURATION_PROMPT,
  PRESCRIBING_PROVIDER_PROMPT,
} from "@/src/models/constants/prompt";
import { DatabaseDefaults } from "@/src/models/database-defaults";
import { DocumentDateSchema } from "@/src/models/documents/document-dates";
import { ExtractedDocumentFields } from "@/src/models/documents/extracted-document-fields";
import { locationSchema } from "@/src/models/location";
import { patientSchema } from "@/src/models/patient";
import { practitionerSchema } from "@/src/models/practitioner";

export const pharmacyRelatedTypeDisplay: Record<PharmacyRelatedDocumentType, string> = {
  [PharmacyRelatedDocumentType.CARE_CONSIDERATION]: "Rx Care Consideration",
  [PharmacyRelatedDocumentType.CLARIFICATION]: "Rx Clarification",
  [PharmacyRelatedDocumentType.CONTROLED_SUBSTANCES_POLICY]: "Controlled Substance Policy",
  [PharmacyRelatedDocumentType.DRUG_MONITORING_PROGRAM]: "PDMP",
  [PharmacyRelatedDocumentType.MED_RECONCILIATION]: "Medication Reconciliation",
  [PharmacyRelatedDocumentType.MEDICATION_LIST]: "Medication List",
  [PharmacyRelatedDocumentType.NON_ADHERENCE]: "Non Adherence",
  [PharmacyRelatedDocumentType.PHARMACY_RELATED]: "Pharmacy Related",
  [PharmacyRelatedDocumentType.REFILL]: "Rx Refill Request",
  [PharmacyRelatedDocumentType.REFILL_MANY]: "Rx Refill Req (multiple meds)",
  [PharmacyRelatedDocumentType.THERAPEUTIC_ALERT]: "Rx Therapeutic Alert",
};

export const pharmacyRelatedTypeSchema = z.nativeEnum(PharmacyRelatedDocumentType);

const basePharmacyRelatedFields = {
  patient: patientSchema,
  receivingLocation: locationSchema.nullable(),
};

const extendedPharmacyRelatedSchema = z.object({
  isAdw: isAdwSchema,
  medications: z.array(z.string().describe(MEDICATION_NAME_PROMPT)).nullable(),
  medicationName: z.string().nullable().describe(MEDICATION_NAME_PROMPT),
  documentType: z.string().nullable(),
  companyName: z.string().nullable().describe(PHARMACY_COMPANY_NAME_PROMPT),
  signatureRequired: z.boolean().nullable(),
  ...DocumentDateSchema.shape,
  provider: practitionerSchema.nullable().describe(PRESCRIBING_PROVIDER_PROMPT),
  receivingProvider: practitionerSchema.nullable(),
  location: locationSchema.nullable(),
  supplyDuration: z.string().nullable().describe(SUPPLY_DURATION_PROMPT),
});

const pharmacyRelatedCustomFields: Record<PharmacyRelatedDocumentType, Record<string, z.ZodTypeAny>> = {
  [PharmacyRelatedDocumentType.CARE_CONSIDERATION]: extendedPharmacyRelatedSchema.pick({
    dateReceived: true,
    provider: true,
    receivingProvider: true,
    location: true,
  }).shape,
  [PharmacyRelatedDocumentType.CLARIFICATION]: extendedPharmacyRelatedSchema.pick({
    dateReceived: true,
    medicationName: true,
    companyName: true,
    provider: true,
    supplyDuration: true,
  }).shape,
  [PharmacyRelatedDocumentType.CONTROLED_SUBSTANCES_POLICY]: extendedPharmacyRelatedSchema.pick({
    isAdw: true,
    dateSigned: true,
  }).shape,
  [PharmacyRelatedDocumentType.DRUG_MONITORING_PROGRAM]: extendedPharmacyRelatedSchema.pick({ dateReceived: true })
    .shape,
  [PharmacyRelatedDocumentType.MED_RECONCILIATION]: extendedPharmacyRelatedSchema.pick({
    dateReceived: true,
    medications: true,
  }).shape,
  [PharmacyRelatedDocumentType.MEDICATION_LIST]: extendedPharmacyRelatedSchema.pick({
    isAdw: true,
    dateReceived: true,
    medications: true,
  }).shape,
  [PharmacyRelatedDocumentType.NON_ADHERENCE]: extendedPharmacyRelatedSchema.pick({
    dateReceived: true,
    medicationName: true,
    provider: true,
    receivingProvider: true,
  }).shape,
  [PharmacyRelatedDocumentType.PHARMACY_RELATED]: extendedPharmacyRelatedSchema.shape,
  [PharmacyRelatedDocumentType.REFILL]: extendedPharmacyRelatedSchema.pick({
    isAdw: true,
    documentType: true,
    companyName: true,
    signatureRequired: true,
    medicationName: true,
    medications: true,
    dateOfDocument: true,
    dateReceived: true,
    provider: true,
    receivingProvider: true,
    location: true,
    supplyDuration: true,
  }).shape,
  [PharmacyRelatedDocumentType.REFILL_MANY]: extendedPharmacyRelatedSchema.pick({
    documentType: true,
    medications: true,
    dateReceived: true,
    provider: true,
    location: true,
  }).shape,
  [PharmacyRelatedDocumentType.THERAPEUTIC_ALERT]: extendedPharmacyRelatedSchema.pick({
    dateReceived: true,
    companyName: true,
    medicationName: true,
    provider: true,
    location: true,
  }).shape,
};

const makePharmacyRelatedSchema = (type: PharmacyRelatedDocumentType, customFields: Record<string, z.ZodTypeAny>) =>
  z.object({
    type: z.literal(type),
    ...basePharmacyRelatedFields,
    ...customFields,
  });

export const pharmacyRelatedSchemas: Record<
  PharmacyRelatedDocumentType,
  ReturnType<typeof makePharmacyRelatedSchema>
> = (Object.values(PharmacyRelatedDocumentType) as PharmacyRelatedDocumentType[]).reduce(
  (acc, type) => {
    acc[type] = makePharmacyRelatedSchema(type, pharmacyRelatedCustomFields[type]);

    return acc;
  },
  {} as Record<PharmacyRelatedDocumentType, ReturnType<typeof makePharmacyRelatedSchema>>
);

const pharmacyRelatedSchemasArray = (Object.values(PharmacyRelatedDocumentType) as PharmacyRelatedDocumentType[]).map(
  (type) => pharmacyRelatedSchemas[type]
) as [ReturnType<typeof makePharmacyRelatedSchema>, ...ReturnType<typeof makePharmacyRelatedSchema>[]];

export const anyPharmacyRelatedSchema = z.discriminatedUnion("type", pharmacyRelatedSchemasArray);

/** See {@link ExtractedDocumentFields} for why the fields are reconstructed rather than inferred. */
export type PharmacyRelated = ExtractedDocumentFields<
  PharmacyRelatedDocumentType,
  typeof extendedPharmacyRelatedSchema,
  typeof basePharmacyRelatedFields
> &
  DatabaseDefaults;

const now = new Date().toISOString();

export const pharmacyRelatedShape: PharmacyRelated = {
  id: "",
  type: PharmacyRelatedDocumentType.PHARMACY_RELATED,
  patient: null,
  receivingLocation: null,
  medications: null,
  medicationName: null,
  documentType: null,
  companyName: null,
  dateReceived: null,
  dateSigned: null,
  createdAt: now,
  updatedAt: now,
  deletedAt: null,
  groupId: "",
};
